import React from "react";
import { Provider as WagmiProvider } from "wagmi";
import { InjectedConnector } from "wagmi/connectors/injected";
import { WalletConnectConnector } from "wagmi/connectors/walletConnect";
import AccountGuard from "guards/AccountGuard";
import ConnectPage from "pages/ConnectPage";
import LocaleProvider from "providers/LocaleProvider";
import { providers } from "ethers";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import {
  chains,
  infuraId,
  locales,
  theme,
  sentryDsn,
  environment,
} from "./config";
import Background from "components/Background";
import Layout from "components/Layout";
import AssetsProvider from "providers/AssetsProvider";
import VestingProvider from "providers/VestingProvider";
import TraitSalesProvider from "providers/TraitSalesProvider";
import TraitsProvider from "providers/TraitsProvider";
import CacheProvider from "providers/CacheProvider";
import ConnectedPage from "pages/ConnectedPage";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [new BrowserTracing()],
    tracesSampleRate: 0.5,
    environment: environment,
    maxValueLength: 1000,
  });
}

const connectors = () => {
  return [
    new InjectedConnector({
      chains,
      options: { shimDisconnect: true },
    }),
    new WalletConnectConnector({
      chains,
      options: {
        infuraId,
        qrcode: true,
      },
    }),
  ];
};

const provider = ({ chainId }: { chainId?: number }) => {
  const network = providers.InfuraProvider.getNetwork(chainId!);
  if (network.ensAddress) {
    return new providers.InfuraProvider(network, infuraId);
  }
  return providers.getDefaultProvider();
};

const extendedTheme = extendTheme(theme);

const App: React.FC = () => (
  <ChakraProvider theme={extendedTheme}>
    <WagmiProvider autoConnect provider={provider} connectors={connectors}>
      <LocaleProvider translations={locales.en}>
        <Background />

        <AccountGuard
          yeyElement={
            <CacheProvider>
              <AssetsProvider>
                <VestingProvider>
                  <TraitsProvider>
                    <TraitSalesProvider>
                      <ConnectedPage />
                    </TraitSalesProvider>
                  </TraitsProvider>
                </VestingProvider>
              </AssetsProvider>
            </CacheProvider>
          }
          neyElement={
            <Layout>
              <ConnectPage />
            </Layout>
          }
        />
      </LocaleProvider>
    </WagmiProvider>
  </ChakraProvider>
);

export default App;
